/* eslint-disable max-lines */
import { TrashIcon } from "@heroicons/react/24/outline";
import { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { ITripType } from "../../../../interfaces/endpoints/trips/Trips.types";
import { timeStringToMinutes } from "../../../../utils/tripDuration";
import Input from "../../../ui/input/Input";
import { SelectOption } from "../../../ui/input/Input.types";
import ReadOnlyClockField from "../../../ui/input/ReadOnlyClockField";
import SelectInput from "../../../ui/input/SelectInput";
import TimePartsField, {
  HOUR_OPTIONS_DURATION,
  pad2
} from "../../../ui/input/TimePartsField";
interface EmptyLineTripProps {
  editMode?: boolean;
  options?: { name: string; id: number }[];
  index: number;
  handleDelete: (id: number) => void;
  tripIndex?: number;
  // Told after the operator sets this stop's distance, so the list can put the stop in travel order.
  onOffsetChange?: (index: number) => void;
  // Told after the operator picks this stop's station, so the list can re-check duplicates.
  onStationChange?: () => void;
}

const MINUTES_IN_DAY = 1440;

const EmptyLineTrip: React.FC<EmptyLineTripProps> = ({
  editMode,
  options,
  index,
  handleDelete,
  tripIndex,
  onOffsetChange,
  onStationChange
}) => {
  const {
    register, getValues, setValue, watch, formState: { errors }
  } = useFormContext<ITripType>();

  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  // A stop is entered the way the trip's own length is: as time after the departure, in the
  // same hour and minute boxes. Its clock is derived from that and shown read-only, with the
  // day it lands on inside the box when that is a later day. The distance is stored in the
  // stop's duration field, which the imported trips already fill with the same number.
  const tripDurationMinutes = watch("trip.durationMinutes");
  const tripDepartureTimeString: string = watch("trip.departureTimeString") ?? "";
  const isMultiDayTrip =
    typeof tripDurationMinutes === "number" && tripDurationMinutes >= MINUTES_IN_DAY;

  const offsetMinutes = watch(`schedules.${index}.duration`);
  const hasOffset =
    typeof offsetMinutes === "number" && !isNaN(offsetMinutes) && offsetMinutes > 0;
  const offsetHour = hasOffset ? pad2(Math.floor(offsetMinutes / 60)) : "";
  const offsetMinute = hasOffset ? pad2(offsetMinutes % 60) : "";

  const clockAfterDeparture = (minutesAfter: number) => {
    const departure = tripDepartureTimeString ? timeStringToMinutes(tripDepartureTimeString) : 0;
    const total = (isNaN(departure) ? 0 : departure) + minutesAfter;
    const clock = `${pad2(Math.floor((total % MINUTES_IN_DAY) / 60))}:${pad2(total % 60)}`;
    return { clock, days: Math.floor(total / MINUTES_IN_DAY) };
  };

  // Whether the stop's distance has been settled, by the prefill below or by the operator typing
  // one; either way the prefill must not run again.
  const prefilledRef = useRef(false);

  const setOffsetPart = (part: "hours" | "minutes", value: string) => {
    const hours = Number(part === "hours" ? value : offsetHour || "00");
    const minutes = Number(part === "minutes" ? value : offsetMinute || "00");
    // Typed by the operator: the prefill must never replace it, even if it has not run yet (a new
    // stop has no clocks to walk) and the trip's departure changes later.
    prefilledRef.current = true;
    const total = hours * 60 + minutes;
    setValue(`schedules.${index}.duration`, total, { shouldValidate: true });
    if (total === 0) {
      // No distance means no clock: the one derived earlier must not reach the server on its own.
      setValue(`schedules.${index}.departureTimeString`, "", { shouldValidate: true });
      setValue(`schedules.${index}.arrivalTimeString`, "");
    }
    onOffsetChange?.(index);
  };

  // Where a loaded stop's distance comes from, once, when the trip's departure is known: on a
  // trip of a day or more the stored number (that is what the server reads there); on a shorter
  // trip the clock walk from the stored clocks (that is what the server reads there, and an
  // imported number can be stale). Each clock earlier than the one before it crossed a midnight.
  useEffect(() => {
    if (prefilledRef.current || !tripDepartureTimeString) return;
    const current = getValues(`schedules.${index}.duration`);
    if (isMultiDayTrip && typeof current === "number" && current > 0) {
      prefilledRef.current = true;
      return;
    }
    const schedules = getValues("schedules") ?? [];
    let previous = timeStringToMinutes(tripDepartureTimeString);
    if (isNaN(previous)) return;
    let running = 0;
    for (let i = 0; i <= index && i < schedules.length; i++) {
      const clockString = schedules[i]?.departureTimeString;
      if (!clockString) return;
      const clock = timeStringToMinutes(clockString);
      if (isNaN(clock)) return;
      running += (clock - previous + MINUTES_IN_DAY) % MINUTES_IN_DAY;
      previous = clock;
    }
    prefilledRef.current = true;
    if (running > 0 && running !== current) setValue(`schedules.${index}.duration`, running);
  }, [isMultiDayTrip, index, tripDepartureTimeString, getValues, setValue]);

  // The stop's clocks always follow departure plus distance, so moving the trip's departure
  // moves every stop with it and the server, which reads the clocks on a trip under a day, sees
  // the same stop the operator does.
  useEffect(() => {
    if (!tripDepartureTimeString) return;
    // The distance is read from the form, not from this render: the prefill above may have just
    // corrected a stale stored number in this same pass, and a clock derived from the stale one
    // would feed the next stop's walk.
    const minutes = Number(getValues(`schedules.${index}.duration`));
    if (!(minutes > 0)) return;
    const { clock } = clockAfterDeparture(minutes);
    if (getValues(`schedules.${index}.departureTimeString`) !== clock) {
      setValue(`schedules.${index}.departureTimeString`, clock, { shouldValidate: true });
      setValue(`schedules.${index}.arrivalTimeString`, clock);
    }
  }, [hasOffset, offsetMinutes, tripDepartureTimeString, index, getValues, setValue]);

  const toggleDropdown = (dropdown: string) => {
    setOpenDropdown((current) => current === dropdown ? null : dropdown);
  };

  const handleOptionSelect = (formField: string, option: SelectOption | null) => {
    setOpenDropdown(null);
    if (!option) return; // cleared with Backspace or Delete
    setValue(formField as any, Number(option.id), { shouldValidate: true });
    onStationChange?.();
  };

  const derived = hasOffset ? clockAfterDeparture(offsetMinutes) : null;
  const offsetError =
    errors?.schedules?.[index]?.duration?.message ??
    errors?.schedules?.[index]?.departureTimeString?.message;

  // The right half keeps the trip row's box sizes and starts right after the station, so the two
  // boxes sit close to it (Arnis: as on dev, not under the trip row's columns). The columns line up
  // from the top, so a validation message under one box cannot push that box up and away from the
  // bin beside it.
  return (
    <div className="flex-row flex items-start justify-between">
      <div className="mb-6 px-2 w-full md:w-1/2">
        {!editMode ? (
          <SelectInput
            options={options || []}
            label="Zgjedh ndalesën"
            placeholder="Zgjedh"
            selectedId={getValues(`schedules.${index}.stationId`)}
            isOpen={openDropdown === `station-${index}`}
            onClick={() => toggleDropdown(`station-${index}`)}
            onOptionSelect={(option) => handleOptionSelect(`schedules.${index}.stationId`, option)}
            error={errors?.schedules?.[index]?.stationId?.message}
          />
        ) : (
          <Input
            {...register(`schedules.${index}.stationId`)}
            label="Ndalesa e zgjedhur"
            className="pointer-events-none"
            inputClassName="h-11"
          />
        )}
      </div>
      <div className="w-full md:w-1/2 flex items-start">
        <div className="mb-6 w-[30%] pr-6">
          <TimePartsField
            id={`schedules-${index}-offset`}
            label="Kohëzgjatja"
            hourOptions={HOUR_OPTIONS_DURATION}
            hours={offsetHour}
            minutes={offsetMinute}
            onPartChange={setOffsetPart}
            error={offsetError}
          />
        </div>
        <div className="mb-6 w-[22%] pr-4">
          <ReadOnlyClockField
            id={`schedules-${index}-clock`}
            label="Arritja në ndalesë"
            clock={derived ? derived.clock : ""}
            days={derived ? derived.days : 0}
          />
        </div>
        {/* The bin sits where a box would: under a blank label of the same height, on the 44px row
            the two boxes share, right after the arrival box. */}
        <div className="mb-6 w-[18%]">
          <div className="mb-1.5 h-[17px]" aria-hidden />
          <div className="h-11 flex items-center">
            <span
              className="transition-colors hover:bg-gray-200 cursor-pointer rounded-full flex
          bg-gray-200 md:bg-transparent
          items-center justify-center w-10 h-9"
              onClick={() => handleDelete(index)}
            >
              <TrashIcon className="w-4 h-4 text-neutral" />
            </span>
          </div>
        </div>
        <input
          hidden
          {...register(`schedules.${index}.arrivalTimeString`)}
        />
        <input
          hidden
          {...register(`schedules.${index}.duration`, { valueAsNumber: true })}
        />
        <input
          value={tripIndex}
          {...register(`schedules.${index}.tripId`)}
          hidden
        />
      </div>
    </div>
  );
};

export default EmptyLineTrip;
